import { ChartsService } from './../../../@core/utils/charts.service';
import { KPI } from './../../../@core/api/models';
import { ProjectsService } from './../../../@core/utils/projects.service';
// tslint:disable-next-line:ordered-imports
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { NbCardModule, NbDialogService } from '@nebular/theme';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ColumnGroupShowType, GridApi, GridOptions } from 'ag-grid-community';
import { BehaviorSubject, Subscription, combineLatest, of } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { InitiativesService, InvestmentScenariiService, SpreadsheetColumnsService } from '../../../@core/utils';
import { AG_GRID_PROJECT_FILTERS, TABLE_FILTER_ICON } from '../../../@core/utils/constants';
import { I18nService } from '../../../@core/utils/i18n.service';
import { AgGridAngular } from 'ag-grid-angular';
import { agIconLoadingTemplate } from '../circular-spinner/circular-spinner';
import { TableFilterComponent } from '../table-filter/table-filter.component';
import { EvolutionRendererComponent } from '../ag-grid-renderers/evolution-renderer.component';
import { BasicDialogComponent } from '../basic-dialog/basic-dialog.component';
import { ProjectFormComponent } from '../side-forms/project-form/project-form.component';
import { DeleteRendererComponent } from '../ag-grid-renderers/delete-button-renderer.component';

@Component({
  selector: 'app-ag-grid-project',
  templateUrl: './ag-grid-project.component.html',
  styleUrls: ['./ag-grid-project.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    AgGridAngular,
    TableFilterComponent,
    TranslateModule,
  ],
})
export class AgGridProjectComponent implements OnInit, OnDestroy {

  @Input() public inputFilters: any;

  constructor(
    private translate: TranslateService,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    public i18n: I18nService,
    private projectsService: ProjectsService,
    private investmentScenariiService: InvestmentScenariiService,
    private initiativesService: InitiativesService,
    private dialogService: NbDialogService,
    private chartsService: ChartsService,
    private rightPanelDialogService: RightPanelDialogService,
  ) { }

  private _reloadData$: BehaviorSubject<void> = new BehaviorSubject(undefined);

  private subscriptions: Subscription[] = [];

  private filtersLocalStorageKey = AG_GRID_PROJECT_FILTERS;

  public gridOptions: GridOptions = {};
  private gridApi: GridApi;
  public filtersNb: number = 0;

  public projectsNb: number = 0;
  public kwhSavings: number = 0;
  public co2Savings: number = 0;

  private scenarii$ = this.investmentScenariiService.selectedScenarii$.pipe(
    map((scenarii) => scenarii.filter((s) => !!s)),
  );
  private scenarii: KPI.InvestmentScenario[] = [];
  private initiativeStatuses: KPI.InitiativeStatus[] = [];

  ngOnInit() {
    this.subscriptions.push(
      this.spreadsheetColumnsService.deleteProjectFilters$.subscribe(
        (deleteFilters) => {
          if (deleteFilters && this.gridApi) {
            Object.keys(this.gridApi.getFilterModel()).map((k) => {
              this.gridApi.destroyFilter(k);
            });
            this.gridApi.onFilterChanged();
            this.gridApi.closeToolPanel();
            localStorage.setItem(
              this.filtersLocalStorageKey,
              JSON.stringify({}),
            );
          }
        },
      ),
      this.spreadsheetColumnsService.openProjectFilters$.subscribe((openFilters) => {
        if (openFilters && this.gridApi) {
          this.gridApi.openToolPanel('columns');
        } else if (!openFilters && this.gridApi) {
          this.gridApi.closeToolPanel();
        }
      }),
      this.spreadsheetColumnsService.getAgGridTranslations().subscribe((translations) => {
        this.gridOptions = {
          ...this.gridOptions,
          overlayLoadingTemplate: agIconLoadingTemplate,
          localeText: translations,
          context: this,
          rowHeight: 28,
          rowBuffer: 10, // Draw 10 rows above and below the visible rows (Performance)
          allowDragFromColumnsToolPanel: true, // Allow user to move columns from panel
          cellSelection: {
            suppressMultiRanges: true,
          },
          rowSelection: 'multiple', // Enable to select the entire row instead of a cell
          getRowId: params => params.data?.id?.toString(),
          suppressPropertyNamesCheck: true,
          columnMenu: 'new',
          components: {
            evolutionRenderer: EvolutionRendererComponent,
          },
          defaultColDef: {
            sortable: true,
            resizable: true,
            editable: false,
            filter: true,
            icons: {
              filter: TABLE_FILTER_ICON,
            },
            menuTabs: [],
            filterParams: {
              excelMode: 'windows',
            },
            suppressHeaderMenuButton: true,
          },
          sideBar: {
            toolPanels: [
              {
                id: 'columns',
                labelDefault: this.translate.instant(
                  'pages.common.ag-grid.columns',
                ),
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                // Hide every other tools
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                },
              },
              {
                id: 'filters',
                labelDefault: this.translate.instant(
                  'pages.common.ag-grid.filters',
                ),
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
              },
            ],
            position: 'right', // Can set the side bar on the left or on the right
            // Set this to open the sidebar by default on columns panel
            // defaultToolPanel: 'columns',
          },
          columnDefs: [],
          animateRows: true, // Row animations (when expanding)
          // Always show the column menus
          suppressMenuHide: true,
          groupHeaderHeight: 28,
          // Restrict aggregations some functions (sum in an ag-grid native one)
          onGridReady: (params) => {
            this.gridApi = params.api;
            this.setColumnDefs();
            // Preload data needed for the context menu before loading the data
            combineLatest([
              this.scenarii$.pipe(tap((scenarii) => this.scenarii = scenarii)),
              this.initiativesService.initiativeStatuses$.pipe(
                tap((statuses) => this.initiativeStatuses = statuses),
              )
            ]).subscribe(() => {
              // Load data
              this.loadData();
            });
          },
          // Hook executed after first data set
          onFirstDataRendered: () => {
            this.gridApi?.setGridOption("loading", false);
            // Move the row number column while it's hidden, then show it
            // This must be done here, as the row number column is provided after the
            // grouping column one, in order to have it first in the table
            this.gridApi.moveColumns(['row_nb'], 0);
            this.gridApi.setColumnsVisible(['row_nb'], true);
            this.applyInputFilters();
          },
          onRowDataUpdated: () => {
            this.gridApi?.setGridOption("loading", false);
          },
          getContextMenuItems: () => this.buildContextMenu(),
          onSortChanged: () => {
            if (this.gridApi) {
              this.gridApi.refreshCells();
            }
          },
          onColumnVisible: () => {
            this.gridApi.moveColumns(['row_nb'], 0);
            this.gridApi.setColumnsVisible(['row_nb'], true);
          },
          onFilterChanged: () => {
            // Check it's not the first loading
            // Retrieve current filters model
            const filterModel = this.gridApi.getFilterModel();
            const filtersNb = Object.keys(filterModel).length;
            this.filtersNb = filtersNb;
          },
          onCellDoubleClicked: (params) => {
            if (params.data?.id) {
              this.openProjectForm(params.data.id);
            }
          },
        };
      }),
    );
  }

  private setColumnDefs() {
    this.subscriptions.push(
      combineLatest([
        this.scenarii$,
        this.initiativesService.relevantEnergyTypes$,
      ]).subscribe(([scenarii, energyTypes]) => {
        this.gridApi.setGridOption(
          'columnDefs',
          [
            {
              field: 'row_nb',
              headerName: '',
              rowDrag: false,
              suppressColumnsToolPanel: true,
              suppressFiltersToolPanel: true,
              filter: false,
              resizable: false,
              sortable: false,
              menuTabs: [],
              valueGetter: this.spreadsheetColumnsService.setRowNumber,
              width: 45,
              minWidth: 45,
              maxWidth: 45,
              pinned: true,
              lockPosition: true,
              enableCellChangeFlash: false,
              cellStyle: { 'text-align': 'center', color: '#707171' },
            },
            {
              field: 'label',
              headerName: this.translate.instant('Project label'),
              pinned: true,
              lockPosition: true,
              width: 220,
              valueFormatter: this.spreadsheetColumnsService.nullFormatter,
            },
            ...scenarii.map((scenario) => ({
              pinned: true,
              headerName: scenario.name,
              headerClass: scenario.is_main ? '' : `scenario-${scenario.id}`,
              children: [
                {
                  field: `project_status_scenario_${scenario.id}`,
                  headerName: this.translate.instant('Status'),
                  width: 150,
                  cellClass: scenario.is_main ? '' : `scenario-${scenario.id}`,
                  headerClass: scenario.is_main ? '' : `scenario-${scenario.id}`,
                  lockPosition: true,
                },
                /* {
                  field: `project_year_scenario_${scenario.id}`,
                  headerName: this.translate.instant('Year'),
                  width: 90,
                  cellClass: `scenario-${scenario.id}`,
                  headerClass: `scenario-${scenario.id}`,
                  lockPosition: true,
                }, */
              ],
            })),
            {
              headerName: this.translate.instant("Desired gains"),
              icons: {
                columnGroupOpened: '<i class="far fa-minus"/>',
                columnGroupClosed: '<i class="far fa-plus"/>',
              },
              children: [
                {
                  field: `gain_all`,
                  headerName: this.translate.instant("Expected global gain"),
                  valueFormatter: this.spreadsheetColumnsService?.kwhFormatter,
                  cellRenderer: 'evolutionRenderer',
                  width: 150,
                  menuTabs: [],
                  //columnGroupShow: <ColumnGroupShowType>'closed',
                },
                ...energyTypes.map((energyType) => ({
                  field: `gain_${energyType.id}`,
                  headerName: energyType.name,
                  width: Math.min(
                    Math.max((energyType.name.length * 8 + 60), 140),
                    240,
                  ),
                  valueFormatter: this.spreadsheetColumnsService?.kwhFormatter,
                  cellRenderer: 'evolutionRenderer',
                  menuTabs: [],
                  columnGroupShow: 'open' as ColumnGroupShowType,
                  headerClass: `energy-type-${energyType.id}`,
                })),
              ]
            },
            {
              field: 'total_price',
              headerName: this.translate.instant('Amount'),
              hide: false,
              width: 150,
              menuTabs: [],
              valueFormatter: this.spreadsheetColumnsService.priceFormatter,
              filter: 'agNumberColumnFilter',
            },
            {
              field: 'investments_count',
              headerName: this.translate.instant('Nb related investments'),
              hide: false,
              width: 200,
              menuTabs: [],
              valueFormatter: this.spreadsheetColumnsService.nullFormatter,
              filter: 'agNumberColumnFilter',
            },
            {
              field: 'start_date',
              headerName: this.translate.instant('Start date'),
              hide: false,
              width: 120,
              valueFormatter: (params) => params.value ? new Date(params.value).getFullYear().toString() : null,
            },
            {
              field: 'end_date',
              headerName: this.translate.instant('End date'),
              hide: false,
              width: 120,
              valueFormatter: (params) => params.value ? new Date(params.value).getFullYear().toString() : null,
            },
            {
              field: 'project_type',
              headerName: this.translate.instant('Project type'),
              width: 170,
              valueFormatter: (params) => params.value.name,
            },
            {
              field: 'site',
              headerName: this.translate.instant(
                'core.utils.spreadsheet_columns.column_site',
              ),
              hide: false,
              width: 170,
              sort: 'asc',
              valueFormatter: (params) => params.value.name,
            },
            {
              field: 'emissions',
              headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.GHG_emissions_avoided_first_year')
                + ' (i)',
              valueFormatter: this.spreadsheetColumnsService.ghgFormatter,
              width: 120,
              filter: 'agNumberColumnFilter',
              headerTooltip: this.translate.instant('pages.energy.initiative.labels.ghg_savings_first_year'),
            },
            {
              field: 'probability',
              headerName: this.translate.instant('Probability'),
              hide: false,
              width: 150,
              refData: this.spreadsheetColumnsService.confidenceMap,
            },
            {
              field: 'real_ratio',
              headerName: this.translate.instant('Real ratio {currency} / m2', {
                currency: this.i18n.getCurrency(),
              }),
              hide: false,
              width: 170,
              menuTabs: [],
              valueFormatter: this.spreadsheetColumnsService.priceFormatterByFlooring,
              filter: 'agNumberColumnFilter',
            },
            {
              field: 'comments',
              headerName: this.translate.instant('Comment'),
              hide: false,
              width: 230,
              menuTabs: [],
              valueFormatter: this.spreadsheetColumnsService.nullFormatter,
            },
            {
              field: 'delete',
              headerName: this.translate.instant('Delete'),
              cellRenderer: DeleteRendererComponent,
              headerClass: 'justify-center',
              width: 100,
              filter: false,
              menuTabs: [],
            },
          ]
        );
      }),
    );
  }

  /**
   * Build the cells context menu
   */
  protected buildContextMenu() {
    const contextMenu = []; // Initialize context menu

    const ranges = this.gridApi?.getCellRanges();

    /**
     * Get data from selected rows
     */

    const rowNodes: {[key: string]: any}[] = ranges?.reduce((acc, curr) => {
      for (let i = curr.startRow.rowIndex; i <= curr.endRow.rowIndex; i++) {
        acc.push(this.gridApi?.getDisplayedRowAtIndex(i));
      }
      return acc;
    }, []);

    const isSingleSelect = rowNodes.length === 1;

    /**
     * Change initiative status
     */
    contextMenu.push({
      name: isSingleSelect
        ? this.translate.instant('Change project status')
        : this.translate.instant('Change projects status'),
      icon: '<i class="fa-light fa-user-check fa-lg"></i>',
      subMenu: this.scenarii.map((scenario) => {
        return {
          name: scenario.name,
          cssClasses: [`scenario-${scenario.id}`],
          subMenu: this.initiativeStatuses.filter((status) =>
            this.isNewStatusDifferent(rowNodes, scenario.id, status.id),
          ).sort((a, b) => a.ordering - b.ordering).map((status) => {
            return {
              name: status.name,
              disabled: !this.hasInstanceForScenario(rowNodes, scenario.id)
                || !this.areStatusEqualForScenario(rowNodes, scenario.id),
              action: () => {
                // If main scenario AND any of the selected initiative has an investment which will be impacted by the change,
                // then display a confirmation dialog, else, directly update the status
                const obs = scenario.is_main
                  ? this.dialogService.open(
                    BasicDialogComponent,
                    {
                      autoFocus: false,
                      context: {
                        title: this.translate.instant('Confirmation'),
                        content: rowNodes.length === 1
                          ? this.translate.instant(
                            'Are you sure you want to change the project to {toProjectStatus}, and update the related investments and actions ?',
                            { toProjectStatus: status.name }
                          )
                          : this.translate.instant(
                            'Are you sure you want to change the {projectNb} project(s) to {toProjectStatus}, and update the related investments and actions ?',
                            {
                              projectNb: rowNodes.length,
                              toProjectStatus: status.name,
                            }
                          ),
                      },
                    }
                  ).onClose
                  : of(true);

                // If the user confirms the change (Or no confirmation is needed), then update the status
                obs.pipe(
                  filter((confirmation) => !!confirmation),
                  tap(() => this.gridApi?.setGridOption("loading", true)),
                  switchMap(() => {
                    return combineLatest(
                      rowNodes.map((rowNode) => {
                        const projectId = rowNode.data.id;
                        return this.projectsService.updateProjectInstance(
                          projectId,
                          rowNode.data.instances.filter((instance) =>
                            instance?.scenario?.id === scenario.id
                          ).map((instance) => ({
                            id: instance.id,
                            status: status.id,
                            scenario: instance.scenario?.id,
                            savings_start_at: instance.savings_start_at,
                          })),
                        );
                      }),
                    )
                  })
                ).subscribe({
                  next: () => {
                    this.reloadData();
                    this.chartsService.capexHasChanged();
                    this.chartsService.energyHasChanged();
                  },
                  error: () => {
                    this.gridApi?.setGridOption("loading", false);
                  }
                });
              },
            }
          }),
        };
      }),
    });

    /**
     * See linked investments
     */
    /* if (projectIds?.length) {
      contextMenu.push(
        {
          name: this.translate.instant('See investments'),
          action: () => {
            const queryParams = {
              [`${this.investFilterPrefix}projects`]: projectIds.join(','),
              ['applyPeriod']: false,
            };
            this.router.navigate(['decision', 'spreadsheet'], { queryParams });
          },
        },
      );
    } */

    return contextMenu;
  }


  private loadData() {
    this.subscriptions.push(
      this._reloadData$.pipe(
        tap(() => this.gridApi?.setGridOption("loading", true)),
        switchMap(() =>
          combineLatest([
            this.investmentScenariiService.selectedScenarii$.pipe(
              map((scenarii) => scenarii.filter((s) => !!s)),
            ),
            this.initiativesService.relevantEnergyTypes$,
            this.projectsService.getAllProjects(),
          ])
        ),
      ).subscribe(([scenarii, energyTypes, projects]) => {
        const rows = projects.map((project) => {
          const otherFields = {};

          // Project scenario status & year
          scenarii.map((scenario) => {
            const instance = project.instances.find((p) => p.scenario.id === scenario.id);
            otherFields[`project_instance_id_scenario_${scenario.id}`] = instance?.id;
            otherFields[`project_status_scenario_${scenario.id}`] = instance?.status?.name;
            /* otherFields[`project_year_scenario_${scenario.id}`] = instance?.savings_start_at
              ? new Date(instance.savings_start_at).getFullYear()
              : null; */
          });

          // Gains
          otherFields[`gain_all`] = 0;
          energyTypes.map((energyType) => {
            const globalEnergyTypeGain = project.gains.find((gain) =>
              gain.energy_type === energyType.id
            );
            const uniqueEnergyTypeGains = project.project_initiatives.map((i) =>
              i.gains.find((g) => g.energy_type_id === energyType.id)
            ).filter((v) => !!v);
            const globalGains = globalEnergyTypeGain?.theoretical_savings_full_year || 0;
            const uniqueGains = uniqueEnergyTypeGains.reduce((acc, gain) => acc + gain.theoretical_savings_full_year, 0);
            otherFields[`gain_all`] += otherFields[`gain_${energyType.id}`] = project.global_gain ? globalGains : uniqueGains;
          });

          // CO2 emissions avoided
          const energyTypeGains = project.project_initiatives.reduce((acc, initiative) =>
            acc.concat(initiative.gains),
            [] as KPI.InitiativeGain[],
          );
          const globalEmissions = energyTypeGains.reduce((acc, gain) => acc + gain.estimated_greenhouse_gas_savings_full_year, 0);
          const uniqueEmissions = energyTypeGains.reduce((acc, gain) => acc + gain.theoretical_greenhouse_gas_savings_full_year, 0);
          otherFields[`emissions`] = project.global_gain ? globalEmissions : uniqueEmissions;

          return {
            ...project,
            ...otherFields,
            start_date: project.start_date ? new Date(project.start_date).getFullYear() : null,
          };
        });

        this.projectsNb = rows.length;
        this.kwhSavings = Math.round(rows.reduce((acc, row) => acc + row['gain_all'], 0)) / 1000;
        this.co2Savings = Math.round(rows.reduce((acc, row) => acc + row['emissions'], 0)) / 1000;
        this.gridApi?.setGridOption('rowData', rows);
        this.gridApi?.redrawRows(); // Without this line, some cell styles can be not refreshed
        this.gridApi?.setGridOption("loading", false);
      }),
    );
  }

  private openProjectForm(projectId: number): void {
    this.subscriptions.push(
      this.projectsService.getProject(Number(projectId)).pipe(first()).subscribe((project) =>
        this.rightPanelDialogService.open(
          ProjectFormComponent,
          {
            autoFocus: false,
            closeOnBackdropClick: false,
            context: {
              project,
            },
          }
        )
      )
    )
  }

  private reloadData() {
    this._reloadData$.next(undefined);
  }

  // Checks that the new status is different from all selected rows old status
  private isNewStatusDifferent(rowNodes: any[], scenarioId: number, newStatusId: number) {
    return rowNodes.every((row) => {
      return row.data?.instances?.filter((instance) =>
        instance.scenario?.id === scenarioId
      ).find((instance) => instance.status.id !== newStatusId);
    });
  }

  // Checks that an instance exists for the given scenario in all selected rows
  private hasInstanceForScenario(rowNodes: any[], scenarioId: number) {
    return rowNodes.every((row) => {
      if (row?.data) {
        return !!row.data.instances?.find((instance) => instance.scenario?.id === scenarioId);
      }
      return false;
    });
  }

  // Checks that all selected rows have the same status for the given scenario
  private areStatusEqualForScenario(rowNodes: any[], scenarioId: number): boolean {
    const status_column = `project_status_scenario_${scenarioId}`;
    return rowNodes.every((rowNode) => {
      if (rowNodes[0]?.data && rowNodes[0].data[status_column]) {
        return (
          rowNode.data &&
          rowNode.data[status_column] === rowNodes[0].data[status_column]
        );
      } else {
        return false;
      }
    });
  }

  private isInputFilter(): boolean {
    return !!(this.inputFilters && Object.keys(this.inputFilters)?.length);
  }

  /**
   * Used to apply filters from Input (Example: displaying the table in a dialog)
   * In this case there Shouldn't be any filters in the URL
   */
  private applyInputFilters(): void {
    if (this.isInputFilter()) {
      const inputFilterKeys = Object.keys(this.inputFilters);
      inputFilterKeys.map((column) => {
        const values = this.inputFilters[column].map((v) => v.toString());

        this.gridApi.getColumnFilterInstance(column).then((filterInstance) => {
          // Apply the set of values as a filter to the column
          filterInstance.setModel({
            filterType: 'set',
            values: values,
          });
        });
      });
    }
  }

  public delete(projectId: number, _projectName: string) {
    this.dialogService.open(
      BasicDialogComponent,
      {
        context: {
          title: this.translate.instant('Bouquet of works deletion'),
          content: this.translate.instant('By clicking OK, you will delete the bouquet of works')
            + '<br/><br/>'
            + this.translate.instant('Objects related to the bouquet of works (investments, etc) will not be deleted'),
        },
        autoFocus: false,
      },
    ).onClose.pipe(
      first(),
      filter((res) => !!res),
      switchMap(() => this.projectsService.deleteProject(projectId)),
    ).subscribe(() => this.reloadData());
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
